// Pure derivation function for the qualification stepper — no i18n dependencies.
// Returns [] when there is no stepper (zero requests, uncapped flow, pre-deadline).
// The select step is only injected when the flow is capped (limits != null).

export type StepState = "done" | "active" | "waiting";

export interface TranslationKey {
  key: string;
  params?: Record<string, string | number>;
}

export interface QualificationStep {
  key: "review" | "select" | "send";
  state: StepState;
  titleKey: string;
  subtitle: TranslationKey | null;
}

// No invitation input: QualificationTab renders the outcome record instead of the stepper
// once the invitation is confirmed, so the derivation never sees that state.
export interface DeriveQualificationStepsInput {
  totalSubmittedOrLater: number;
  unresolvedCount: number;
  qualifiedCount: number;
  capped: boolean;
  selectedCount: number;
  selectionValid: boolean;
  missingReasonCount: number;
  overMax: boolean;
  underFloor: boolean;
}

export const deriveQualificationSteps = (
  input: DeriveQualificationStepsInput,
): QualificationStep[] => {
  const {
    totalSubmittedOrLater,
    unresolvedCount,
    qualifiedCount,
    capped,
    selectedCount,
    selectionValid,
    missingReasonCount,
    overMax,
    underFloor,
  } = input;

  // No stepper when there are no requests (the "0 av 0" case) or when uncapped.
  if (totalSubmittedOrLater === 0 || !capped) return [];

  const reviewDone = unresolvedCount === 0 && totalSubmittedOrLater > 0;

  if (!reviewDone) {
    // Step 1: review active
    const reviewedCount = totalSubmittedOrLater - unresolvedCount;
    return [
      {
        key: "review",
        state: "active",
        titleKey: "Review the requests",
        subtitle: {
          key: "{{reviewed}} of {{total}} reviewed",
          params: { reviewed: reviewedCount, total: totalSubmittedOrLater },
        },
      },
      {
        key: "select",
        state: "waiting",
        titleKey: "Choose who to invite",
        subtitle: { key: "Waiting for the reviews" },
      },
      {
        key: "send",
        state: "waiting",
        titleKey: "Send the invitation",
        subtitle: { key: "The bid deadline is set here" },
      },
    ];
  }

  // Review is done. Check select step.
  if (!selectionValid) {
    const selectSubtitle: TranslationKey = (() => {
      if (missingReasonCount > 0) {
        return { key: "{{count}} reasons missing", params: { count: missingReasonCount } };
      }
      if (overMax) {
        return { key: "Too many selected" };
      }
      if (underFloor) {
        return { key: "Too few selected" };
      }
      // Defensive fallback — selection invalid without a specific signal.
      return { key: "Complete the selection above before sending." };
    })();

    return [
      {
        key: "review",
        state: "done",
        titleKey: "Review the requests",
        subtitle: {
          key: "{{reviewed}} of {{total}} reviewed",
          params: { reviewed: totalSubmittedOrLater, total: totalSubmittedOrLater },
        },
      },
      {
        key: "select",
        state: "active",
        titleKey: "Choose who to invite",
        subtitle: selectSubtitle,
      },
      {
        key: "send",
        state: "waiting",
        titleKey: "Send the invitation",
        subtitle: { key: "The bid deadline is set here" },
      },
    ];
  }

  // Review done, selection valid → send active
  return [
    {
      key: "review",
      state: "done",
      titleKey: "Review the requests",
      subtitle: {
        key: "{{reviewed}} of {{total}} reviewed",
        params: { reviewed: totalSubmittedOrLater, total: totalSubmittedOrLater },
      },
    },
    {
      key: "select",
      state: "done",
      titleKey: "Choose who to invite",
      subtitle: {
        key: "{{selected}} of {{qualified}} qualified",
        params: { selected: selectedCount, qualified: qualifiedCount },
      },
    },
    {
      key: "send",
      state: "active",
      titleKey: "Send the invitation",
      subtitle: null,
    },
  ];
};
